import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { FiSearch, FiEye, FiMessageCircle, FiTrash2, FiFile, FiFileText } from 'react-icons/fi';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import PinModal from '../components/PinModal';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const SalesList = () => {
  const { user } = useAuth();
  const [sales, setSales] = useState([]);
  const [filteredSales, setFilteredSales] = useState([]);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedSale, setSelectedSale] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [saleToDelete, setSaleToDelete] = useState(null);

  const isAdmin = user?.role === 'admin';
  const canView = user?.role === 'admin' || user?.role === 'caissier';
  const canDelete = isAdmin;

  useEffect(() => { if (canView) fetchSales(); }, []);
  useEffect(() => { filterSales(); }, [search, startDate, endDate, status, sales]);

  const fetchSales = async () => {
    try {
      const response = await api.get('/sales');
      setSales(response.data);
      setFilteredSales(response.data);
    } catch (error) { toast.error('Erreur lors du chargement des ventes'); } finally { setLoading(false); }
  };

  const filterSales = () => {
    let filtered = [...sales];
    if (search) filtered = filtered.filter(s => s.invoice_number?.toLowerCase().includes(search.toLowerCase()) || s.client_name?.toLowerCase().includes(search.toLowerCase()));
    if (startDate) filtered = filtered.filter(s => new Date(s.created_at) >= startDate);
    if (endDate) filtered = filtered.filter(s => new Date(s.created_at) <= endDate);
    if (status) filtered = filtered.filter(s => s.status === status);
    setFilteredSales(filtered);
  };

  const viewDetails = async (saleId) => {
    try {
      const response = await api.get(`/sales/${saleId}`);
      setSelectedSale(response.data);
      setShowDetailModal(true);
    } catch (error) { toast.error('Erreur lors du chargement des détails'); }
  };

  const handleDelete = async () => {
    if (!saleToDelete) return;
    try {
      await api.delete(`/sales/${saleToDelete.id}`);
      toast.success('Vente supprimée avec succès');
      setShowPinModal(false);
      setSaleToDelete(null);
      fetchSales();
    } catch (error) { toast.error('Erreur lors de la suppression'); }
  };

  const exportToExcel = () => {
    const exportData = filteredSales.map(s => ({ 'Facture': s.invoice_number, 'Client': s.client_name, 'Date': new Date(s.created_at).toLocaleDateString('fr-FR'), 'Total': s.total_amount, 'Payé': s.paid_amount, 'Statut': s.status === 'paid' ? 'Payé' : s.status === 'partial' ? 'Partiel' : 'En attente' }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    XLSX.writeFile(XLSX.utils.book_new(), ws, `ventes_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Export Excel réussi');
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setFillColor(16, 185, 129);
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setFontSize(18);
    doc.text('MULTI SERVICE TECHNOLOGIES', pageWidth / 2, 20, { align: 'center' });
    doc.setFontSize(10);
    doc.text('Liste des ventes', pageWidth / 2, 32, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    const total = filteredSales.reduce((sum, s) => sum + (s.total_amount || 0), 0);
    doc.text(`Total ventes: ${filteredSales.length} | Montant: ${total.toLocaleString()} FCFA`, 14, 55);
    const tableHeaders = [['Facture', 'Client', 'Date', 'Total', 'Statut']];
    const tableRows = filteredSales.slice(0, 50).map(s => [s.invoice_number, s.client_name || '-', new Date(s.created_at).toLocaleDateString('fr-FR'), `${s.total_amount.toLocaleString()} FCFA`, s.status === 'paid' ? 'Payé' : 'Partiel']);
    doc.autoTable({ startY: 65, head: tableHeaders, body: tableRows, theme: 'striped', styles: { fontSize: 8 }, headStyles: { fillColor: [16, 185, 129], textColor: [255, 255, 255] } });
    doc.save(`ventes_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('Export PDF réussi');
  };

  const formatAmount = (amount) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XAF' }).format(amount || 0);
  const formatDate = (date) => date ? new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
  const getStatusBadge = (status) => ({ paid: 'bg-green-100 text-green-800', partial: 'bg-orange-100 text-orange-800', pending: 'bg-red-100 text-red-800' }[status] || 'bg-gray-100');
  const getStatusLabel = (status) => ({ paid: 'Payé', partial: 'Partiel', pending: 'En attente' }[status] || status);

  if (!canView) { navigate('/dashboard'); return null; }
  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Liste des ventes</h1>
        <div className="flex gap-3">
          <button onClick={exportToExcel} className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"><FiFile size={18} /> Excel</button>
          <button onClick={exportToPDF} className="bg-red-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"><FiFileText size={18} /> PDF</button>
        </div>
      </div>

      <div className="card mb-6"><div className="grid grid-cols-1 md:grid-cols-4 gap-4"><div className="relative"><FiSearch className="absolute left-3 top-1/2 text-gray-400" /><input type="text" placeholder="Facture ou client..." className="input-field pl-10" value={search} onChange={(e) => setSearch(e.target.value)} /></div><DatePicker placeholderText="Date début" selected={startDate} onChange={setStartDate} className="input-field" dateFormat="dd/MM/yyyy" isClearable /><DatePicker placeholderText="Date fin" selected={endDate} onChange={setEndDate} className="input-field" dateFormat="dd/MM/yyyy" isClearable /><select className="input-field" value={status} onChange={(e) => setStatus(e.target.value)}><option value="">Tous</option><option value="paid">Payé</option><option value="partial">Partiel</option><option value="pending">En attente</option></select></div></div>

      <div className="card overflow-x-auto"><table className="w-full"><thead className="border-b"><tr><th className="pb-3">Facture</th><th className="pb-3">Client</th><th className="pb-3">Date</th><th className="pb-3 text-right">Total</th><th className="pb-3 text-right">Payé</th><th className="pb-3 text-center">Statut</th><th className="pb-3 text-center">Actions</th></tr></thead><tbody>{filteredSales.map((sale) => (<tr key={sale.id} className="border-b hover:bg-gray-50"><td className="py-3 font-medium">{sale.invoice_number}</td><td>{sale.client_name || 'Anonyme'}</td><td className="text-sm">{formatDate(sale.created_at)}</td><td className="text-right text-green-600">{formatAmount(sale.total_amount)}</td><td className="text-right">{formatAmount(sale.paid_amount)}</td><td className="text-center"><span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(sale.status)}`}>{getStatusLabel(sale.status)}</span></td><td className="text-center"><div className="flex justify-center gap-2"><button onClick={() => viewDetails(sale.id)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"><FiEye size={16} /></button>{canDelete && <button onClick={() => { setSaleToDelete(sale); setShowPinModal(true); }} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><FiTrash2 size={16} /></button>}</div></td></tr>))}</tbody></table>{filteredSales.length === 0 && <div className="text-center py-8"><p className="text-gray-500">Aucune vente trouvée</p></div>}</div>

      {showDetailModal && selectedSale && (<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto"><div className="bg-white rounded-xl p-6 w-full max-w-2xl"><div className="flex justify-between mb-4"><h2 className="text-xl font-semibold">Détails facture {selectedSale.invoice_number}</h2><button onClick={() => setShowDetailModal(false)}>✕</button></div><div className="bg-green-50 p-4 rounded-lg mb-4"><p><strong>Client:</strong> {selectedSale.client_name}</p><p><strong>Date:</strong> {formatDate(selectedSale.created_at)}</p></div><table className="w-full mb-4"><thead className="border-b"><tr><th>Produit</th><th className="text-center">Qté</th><th className="text-right">Prix</th><th className="text-right">Total</th></tr></thead><tbody>{selectedSale.items?.map((item, idx) => (<tr key={idx} className="border-b"><td>{item.product_name}</td><td className="text-center">{item.quantity}</td><td className="text-right">{formatAmount(item.unit_price)}</td><td className="text-right">{formatAmount(item.total)}</td></tr>))}</tbody></table><div className="text-right"><p>Total: <span className="font-bold text-green-600">{formatAmount(selectedSale.total_amount)}</span></p><p>Payé: {formatAmount(selectedSale.paid_amount)}</p></div></div></div>)}

      <PinModal isOpen={showPinModal} onClose={() => { setShowPinModal(false); setSaleToDelete(null); }} onConfirm={handleDelete} title="Supprimer une vente" message="Cette action est irréversible." />
    </div>
  );
};

export default SalesList;